import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Home } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';

import AuditModal from './AuditModal';
import CodeTableForm from './CodeTableForm';
import { buildColumns } from './columns';
import { configFor } from './config';
import ImportModal from './ImportModal';
import { createRow, deleteRow, importRows, listPickList, listRows, updateRow } from './operations';
import { isUnseenValue, knownValues, pickListSummary, toCsvData } from './utils';
import {
  Breadcrumbs,
  Button,
  ExportCsvButton,
  Footer,
  SubHeader,
  TableFilter,
  TablePagination,
} from '../../components';
import { newDateFormatted } from '../../utils';

import './styles.scoped.css';

/**
 * CP-857: maintenance for the TenStreet code tables, for the BI Admin role.
 *
 * ONE screen for both tables — `type` selects which, the same way TableContent is
 * parameterized. Everything table-specific lives in config.js; adding a third code table
 * means a config entry, a route and a menu item, with no new screen and no new audit
 * plumbing (tb_cl_cp_bi_codetable_audit is shared).
 *
 * CENTERLINE ONLY. The data is TenStreet, which PRST does not have, so the routes and the
 * Admin Portal buttons are gated on the brand — see routes/index.js and adminSlice.js.
 *
 * Local state only — this is a maintenance/search screen, so no Redux slice (house rule).
 */
const BiCodeTable = ({ type }) => {
  const navigate = useNavigate();
  const config = useMemo(() => configFor(type), [type]);

  const [rows, setRows] = useState([]);
  const [pickList, setPickList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [auditKey, setAuditKey] = useState(undefined);
  const [auditOpen, setAuditOpen] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    setError('');
    return listRows(config)
      .then(setRows)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [config]);

  useEffect(() => {
    refresh();
    listPickList(config).then(setPickList);
  }, [config, refresh]);

  // The values TenStreet has actually sent. Drives the "not in TenStreet" tag and the
  // form's warning; never constrains input, because the procs accept an unseen value on
  // purpose.
  const known = useMemo(() => knownValues(config, pickList), [pickList, config]);
  const isUnseen = useCallback(value => isUnseenValue(value, known), [known]);

  const columns = useMemo(
    () =>
      buildColumns({
        config,
        isUnseen,
        onEdit: setEditing,
        onHistory: key => {
          setAuditKey(key);
          setAuditOpen(true);
        },
      }),
    [config, isUnseen],
  );

  const tableInstance = useTable(
    {
      columns,
      data: rows,
      // sortBy entries need an explicit desc; react-table treats a missing one as undefined
      // rather than false.
      initialState: { sortBy: [{ id: config.keyField, desc: false }], pageSize: 25 },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow, setFilter, setGlobalFilter } =
    tableInstance;

  // TableFilter keys its dropdown by `Header` and reserves the empty key for its own "All"
  // option, so the action column (no Header, no accessor) must never reach it: selecting
  // "All" would otherwise match that column and call setFilter(undefined, ...), which
  // throws "Could not find a column with id: undefined".
  const filterableColumns = useMemo(() => columns.filter(column => column.Header && column.accessor), [columns]);

  const filter = (event, columnName) => {
    const filterColumn = filterableColumns.find(column => column.Header === columnName);
    if (filterColumn) {
      setFilter(filterColumn.accessor, event.target.value);
    } else {
      setGlobalFilter(event.target.value);
    }
  };

  // Each handler resolves only after the grid has been refreshed, so the dialog stays open
  // (and keeps showing its spinner) until the new state is on screen. Rejections propagate
  // to the dialog, which renders the server's message.
  //
  // The success notice uses the server's own message: the procs append advisory notes to it
  // ("TenStreet has never sent this worklist"), and dropping them would hide the one signal
  // that a save, while accepted, will not do anything.
  const handleCreate = async row => {
    const result = await createRow(config, row);
    setAdding(false);
    setNotice(result?.Message || `${row[config.keyField]} added.`);
    await refresh();
  };

  const handleUpdate = async row => {
    const result = await updateRow(config, row, editing[config.keyField]);
    setEditing(null);
    setNotice(result?.Message || `${row[config.keyField]} saved.`);
    await refresh();
  };

  const handleDelete = async () => {
    const deleted = editing[config.keyField];
    const result = await deleteRow(config, deleted);
    setEditing(null);
    setNotice(result?.Message || `${deleted} deleted. Its previous values are in the change history.`);
    await refresh();
  };

  const handleImport = async ({ mode, rows: importedRows }) => {
    const result = await importRows(config, { mode, rows: importedRows });
    setImporting(false);
    setNotice(result?.Message || 'Import complete.');
    await refresh();
  };

  const fileName = useMemo(() => `${config.type.toLowerCase()}-${newDateFormatted.toString()}.csv`, [config.type]);

  // What the pick-list says about this table's coverage. Phrased per table, because a bare
  // count of values with no row would misread as a backlog on both -- see config.js.
  const summary = useMemo(() => pickListSummary(config, pickList), [pickList, config]);

  return (
    <>
      <SubHeader
        text="Admin Portal"
        buttons={[{ icon: <Home className="subheader-icon" />, onPress: () => navigate(-1) }]}
      />
      <Breadcrumbs leftTitle="Admin Portal" rightTitle={config.title} route="/admin-portal" />

      <div className="wrap">
        <div className="toolbar">
          <div className="toolbar-actions">
            <Button variant="primary" text="Add Row" onPress={() => setAdding(true)} disabled={loading} />
            <Button variant="secondary" text="Import" onPress={() => setImporting(true)} disabled={loading} />
            <Button
              variant="outline"
              text="Change History"
              onPress={() => {
                setAuditKey(undefined);
                setAuditOpen(true);
              }}
            />
            <ExportCsvButton data={toCsvData(config, rows)} filename={fileName} />
          </div>
          <p className="row-count">{loading ? 'Loading…' : `${rows.length} row${rows.length === 1 ? '' : 's'}`}</p>
        </div>

        <p className="screen-note">{config.intro} Changes take effect in BI at the next dataset refresh.</p>

        {!!summary && <p className="screen-note">{summary}</p>}

        {!!notice && <p className="screen-notice">{notice}</p>}
        {!!error && <p className="form-error">{error}</p>}

        {!loading && !error && (
          <>
            <div className="table-controls">
              <TableFilter columns={filterableColumns} onSearchChange={filter} />
              <TablePagination tableInstance={tableInstance} />
            </div>

            <div className="table-frame">
              {rows.length ? (
                <table className="data-table" {...getTableProps()}>
                  <thead>
                    {headerGroups.map(headerGroup => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => (
                          <th
                            {...column.getHeaderProps(
                              column.getSortByToggleProps({
                                title: column.hint || 'Sort by this column',
                                className: column.hint ? 'has-hint' : undefined,
                              }),
                            )}>
                            {column.render('Header')}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {page.map(row => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()}>
                          {row.cells.map(cell => (
                            <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p className="table-empty">No rows yet. Use Import to load the list, or Add Row to start one.</p>
              )}
            </div>
          </>
        )}
      </div>

      {(adding || !!editing) && (
        <CodeTableForm
          config={config}
          row={editing || {}}
          isEdit={!!editing}
          known={known}
          onSave={editing ? handleUpdate : handleCreate}
          onDelete={handleDelete}
          onCancel={() => {
            setAdding(false);
            setEditing(null);
          }}
        />
      )}

      {importing && <ImportModal config={config} onImport={handleImport} onCancel={() => setImporting(false)} />}

      {auditOpen && <AuditModal config={config} keyValue={auditKey} onClose={() => setAuditOpen(false)} />}

      <Footer />
    </>
  );
};

export default BiCodeTable;
